import firebase from 'firebase/app';
import 'firebase/functions';
import 'firebase/firestore';
import { JoyDataServiceAction, JoyDataServiceQueryOption, JoyObjectType, JoyField, JoyProperty, isJoyPropertiesArray, isJoyStatusArray, capitalize, isString, isNumber, isDate, isBoolean, isStringArray, } from 'joy-core';
import FirestoreDocumentListener from '../listeners/FirestoreDocumentListener';
import FirestoreCollectionListener from '../listeners/FirestoreCollectionListener';
import FirestoreQueryListener from '../listeners/FirestoreQueryListener';
import { fromFirestore, toFirestore, toFirestoreAction, toFirestoreObjectType, toFirestoreProperty, toFirestoreStatus, } from '../support/FirestoreConversion';
import { callAPI } from '../support/JoyAPISupport';
export default class FirestoreDataServiceProvider {
    constructor() {
        this.invalidInputDataError = Error('Invalid input data');
    }
    collectionFor(objectType, data) {
        let groupId = data[JoyField.group];
        switch (objectType) {
            case JoyObjectType.activity:
                if (groupId !== null) {
                    return firebase.firestore().collection('groups').doc(groupId).collection('feed');
                }
                return null;
            case JoyObjectType.album:
                if (groupId !== null) {
                    let collection = firebase.firestore().collection('groups').doc(groupId).collection('albums');
                    let albumId = data[JoyField.album];
                    let props = data[JoyField.props];
                    if (isString(albumId) && isJoyPropertiesArray(props)) {
                        if (props.indexOf(JoyProperty.watermark) >= 0 || props.indexOf(JoyProperty.photosession) >= 0) {
                            collection = collection.doc(albumId).collection('security');
                        }
                    }
                    return collection;
                }
                return null;
            case JoyObjectType.comment:
                let mediumId = data[JoyField.medium];
                if (groupId !== null && mediumId !== null) {
                    return firebase
                        .firestore()
                        .collection('groups')
                        .doc(groupId)
                        .collection('media')
                        .doc(mediumId)
                        .collection('comments');
                }
                return null;
            case JoyObjectType.group:
                return firebase.firestore().collection('groups');
            case JoyObjectType.medium:
                if (groupId !== null) {
                    let collection = firebase.firestore().collection('groups').doc(groupId).collection('media');
                    let mediumId = data[JoyField.medium];
                    let props = data[JoyField.props];
                    if (isString(mediumId) && isJoyPropertiesArray(props)) {
                        if (props.indexOf(JoyProperty.watermark) >= 0 || props.indexOf(JoyProperty.photosession) >= 0) {
                            collection = collection.doc(mediumId).collection('security');
                        }
                    }
                    return collection;
                }
                return null;
            case JoyObjectType.user:
                return firebase.firestore().collection('users');
            default:
                return null;
        }
    }
    queryFor(objectType, data, options, collection) {
        let query = null;
        let properties = [];
        let statuses = [];
        let isPublic = data[JoyField.isPublic];
        if (isJoyPropertiesArray(data[JoyField.props])) {
            for (const prop of data[JoyField.props]) {
                properties.push(toFirestoreProperty(prop));
            }
        }
        if (isJoyStatusArray(data[JoyField.status])) {
            for (const status of data[JoyField.status]) {
                statuses.push(toFirestoreStatus(status));
            }
        }
        switch (objectType) {
            case JoyObjectType.activity:
                query = collection.orderBy('createdAt', 'desc');
                break;
            case JoyObjectType.album:
                query = collection;
                if (properties.length > 0) {
                    query = query.where('props', 'array-contains-any', properties);
                }
                if (isBoolean(isPublic)) {
                    query = query.where('isPublic', '==', isPublic);
                }
                query = query.orderBy('slug', 'asc');
                break;
            case JoyObjectType.comment:
                query = collection;
                if (statuses.length > 0) {
                    query = query.where('status', 'in', statuses);
                }
                query = query.orderBy('updatedAt', 'desc');
                break;
            case JoyObjectType.group:
                query = collection;
                let userId = data[JoyField.user];
                if (isString(userId)) {
                    query = query.where('users', 'array-contains', userId);
                }
                let name = data[JoyField.name];
                if (isString(name)) {
                    query = query.where('name', '==', name);
                }
                let description = data[JoyField.description];
                if (isString(description)) {
                    query = query.where('description', '==', description);
                }
                let dealIds = data[JoyField.deals];
                if (isStringArray(dealIds)) {
                    query = query.where('dealId', 'in', dealIds);
                }
                break;
            case JoyObjectType.medium:
                let albumId = data[JoyField.album];
                if (isString(albumId)) {
                    query = collection.where('albums', 'array-contains', albumId);
                }
                else {
                    query = collection;
                }
                if (statuses.length > 0) {
                    query = query.where('status', 'in', statuses);
                }
                if (isBoolean(isPublic)) {
                    query = query.where('isPublic', '==', isPublic);
                }
                query = query.orderBy('takenAt', 'asc');
                break;
            case JoyObjectType.user:
                query = collection;
                let groupId = data[JoyField.group];
                if (isString(groupId)) {
                    query = query.where('groups.' + groupId, '>=', '');
                }
                if (properties.length > 0) {
                    query = query.where('props', 'array-contains-any', properties);
                }
                break;
            default:
                break;
        }
        //Options
        let createdAt = options[JoyDataServiceQueryOption.createdAt];
        if (isDate(createdAt)) {
            query = query.startAfter(createdAt);
        }
        let slug = options[JoyDataServiceQueryOption.slug];
        if (isString(slug)) {
            if (slug !== '') {
                query = query.startAfter(slug);
            }
        }
        let limit = options[JoyDataServiceQueryOption.limit];
        if ((isNumber(limit), limit > 0)) {
            query = query.limit(limit);
        }
        return query;
    }
    async fetchDocument(collection, identifier) {
        let result = await collection.doc(identifier).get();
        return [result.data(), result.id];
    }
    async fetchQuery(query) {
        let result = await query.get();
        let data = result.docs.map((docSnapshot) => [docSnapshot.data(), docSnapshot.id]);
        return data;
    }
    async call(func, data) {
        let result = await firebase.functions().httpsCallable(func)(data ? toFirestore(data) : undefined);
        if (result.data !== null) {
            return fromFirestore(result.data);
        }
        return new Map();
    }
    create(data, options) {
        if (options) {
            return this.performWithOptions(JoyDataServiceAction.create, data, options);
        }
        return this.perform(JoyDataServiceAction.create, data);
    }
    read(data, options) {
        if (options) {
            return this.performWithOptions(JoyDataServiceAction.read, data, options);
        }
        return this.perform(JoyDataServiceAction.read, data);
    }
    update(data, options) {
        if (options) {
            return this.performWithOptions(JoyDataServiceAction.update, data, options);
        }
        return this.perform(JoyDataServiceAction.update, data);
    }
    delete(data, options) {
        if (options) {
            return this.performWithOptions(JoyDataServiceAction.delete, data, options);
        }
        return this.perform(JoyDataServiceAction.delete, data);
    }
    async perform(action, data) {
        let objectType = data[JoyField.objectType];
        if (action === JoyDataServiceAction.read) {
            let collection = this.collectionFor(objectType, data);
            let identifier = data[JoyField.id];
            let props = data[JoyField.props];
            if (isJoyPropertiesArray(props)) {
                if (props.indexOf(JoyProperty.photosession) >= 0) {
                    identifier = 'original';
                }
                else if (props.indexOf(JoyProperty.watermark) >= 0) {
                    identifier = 'watermark';
                }
            }
            if (collection === null || identifier === null) {
                throw this.invalidInputDataError;
            }
            let result = await this.fetchDocument(collection, identifier);
            return fromFirestore(result[0], result[1]);
        }
        if (action === JoyDataServiceAction.update && objectType === JoyObjectType.group) {
            if (isStringArray(data[JoyField.emails])) {
                return this.call('httpsInviteUsers', data);
            }
            if (isStringArray(data[JoyField.users])) {
                return this.call('httpsRemoveUsers', data);
            }
        }
        let method = toFirestoreAction(action) + capitalize(toFirestoreObjectType(objectType));
        let params = toFirestore(data);
        let result = await callAPI(method, params);
        if (result !== null) {
            return fromFirestore(result);
        }
        else {
            return new Map();
        }
    }
    async performWithOptions(action, data, options) {
        let objectType = data[JoyField.objectType];
        if (action === JoyDataServiceAction.read) {
            let collection = this.collectionFor(objectType, data);
            if (collection === null) {
                throw this.invalidInputDataError;
            }
            else {
                let query = this.queryFor(objectType, data, options, collection);
                if (query === null) {
                    throw this.invalidInputDataError;
                }
                else {
                    let result = await this.fetchQuery(query);
                    let docsData = result.map((data) => fromFirestore(data[0], data[1]));
                    return docsData;
                }
            }
        }
        else {
            throw this.invalidInputDataError;
        }
    }
    //Search
    async lookup(data) {
        let objectType = data[JoyField.objectType];
        if (objectType === JoyObjectType.user) {
            return this.call('httpsUserExists', data);
        }
        else {
            throw this.invalidInputDataError;
        }
    }
    //Active updates
    documentListener(data, action) {
        let objectType = data[JoyField.objectType];
        let collection = this.collectionFor(objectType, data);
        if (collection === null) {
            throw Error('Invalid collection');
        }
        let identifier = data[JoyField.id];
        if (identifier === null) {
            throw Error('Invalid identifier');
        }
        return new FirestoreDocumentListener(collection.doc(identifier), action, fromFirestore);
    }
    documentsListener(data, options, action) {
        let objectType = data[JoyField.objectType];
        let collection = this.collectionFor(objectType, data);
        if (collection === null) {
            throw Error('Invalid collection');
        }
        let query = this.queryFor(objectType, data, options, collection);
        if (query === null) {
            return new FirestoreCollectionListener(collection, action, fromFirestore);
        }
        return new FirestoreQueryListener(query, action, fromFirestore);
    }
}
//# sourceMappingURL=FirestoreDataServiceProvider.js.map